import { Button } from "@blurb/codex-react";
import { MantineProvider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";

import { Pagination } from "../../../components/Pagination";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { SearchBar } from "../../../components/search-filter/SearchFilterBar";
import { customerColumns } from "../../../configuration/columns/customer.column";
import { customerFilters } from "../../../configuration/filters/customer.filter";
import { featureFlags } from "../../../featureFlags";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../../utils/graphql.util";

export const CustomersView: React.FC = () => {
  const { classes } = usePageShellStyles();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.AdminSearchCustomersQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.AdminSearchCustomersQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.AdminSearchCustomersQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const { data, isLoading, error, refetch } = GraphQL.useAdminSearchCustomersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.adminSearchCustomers.totalCount),
    }
  );
  const organizations = data?.adminSearchCustomers.data;

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="organizations-view" aria-label="Organizations view">
        <Heading as="h1" size="6xl">
          Organizations
        </Heading>
        <div className={classes.row}>
          {featureFlags.newSearchExperience ? (
            <SearchBar
              placeholder="Search by Public ID or Admin Email"
              isLoading={isLoading}
              onSearchSubmit={setSearchGeneral}
            />
          ) : (
            <RpiSearchFilter
              placeholder="Search by Public ID (or Email)"
              filters={customerFilters}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}
          <Button variant="filled" intent="primary" onClick={() => navigateRoute(["customer:create"])}>
            <IconV3 icon="add" color="inherit" />
            Add new organization
          </Button>
        </div>
        <RpiTableContainer
          data-testid="customers-table"
          footer={<Pagination isLoading={isLoading} pagination={pagination} label="Organizations" />}
        >
          <RpiTable
            emptyMessage={
              searchInputs?.length || searchGeneral ? "No organizations found." : "Organizations will appear here."
            }
            isLoading={isLoading}
            error={error}
            perPage={organizations?.length}
            data={organizations}
            columns={customerColumns}
            withSortBy={{ onChange: setSortInput }}
          />
        </RpiTableContainer>
      </div>
      {outlet}
    </MantineProvider>
  );
};
